import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody } from 'react-bootstrap';
import styled from 'styled-components';

const ModernModal = styled(Modal)`
  .modal-content {
    background: linear-gradient(135deg, #0f172a 0%, #1a1f3a 100%);
    border: 1px solid rgba(81, 194, 201, 0.3);
    border-radius: 12px;
    color: #ffffff;
  }
  
  .modal-header {
    border-bottom: 1px solid rgba(81, 194, 201, 0.2);
    
    h5 {
      color: #ffffff;
      font-weight: 600;
    }
    
    .btn-close {
      filter: invert(1);
    }
  }
  
  .modal-body {
    .form-control {
      background: rgba(15, 23, 42, 0.6);
      border: 1px solid rgba(81, 194, 201, 0.3);
      color: #ffffff;
      border-radius: 8px;
      padding: 0.75rem 1rem;
      
      &::placeholder {
        color: #64748b;
      }
      
      &:focus {
        background: rgba(15, 23, 42, 0.8);
        border-color: #51c2c9;
        color: #ffffff;
        box-shadow: 0 0 0 3px rgba(81, 194, 201, 0.1);
      }
      
      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }
    
    textarea.form-control {
      resize: vertical;
    }
    
    .btn-custom {
      background: linear-gradient(135deg, #51c2c9 0%, #2eb8bf 100%);
      border: none;
      padding: 0.75rem 2rem;
      border-radius: 8px;
      font-weight: 600;
      transition: all 0.3s ease;
      
      &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 4px 15px rgba(81, 194, 201, 0.4);
      }
      
      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }
    
    p {
      color: #94a3b8;
    }
    
    .success-message {
      background: rgba(81, 194, 201, 0.15);
      border: 1px solid rgba(81, 194, 201, 0.4);
      border-radius: 8px;
      padding: 1rem;
      margin-bottom: 1rem;
      color: #51c2c9;
      font-weight: 500;
      text-align: center;
    }
    
    .error-message {
      background: rgba(242, 71, 52, 0.15);
      border: 1px solid rgba(242, 71, 52, 0.4);
      border-radius: 8px;
      padding: 1rem;
      margin-bottom: 1rem;
      color: #f24734;
      font-weight: 500;
      text-align: center;
    }
  }
`;

interface ContactModalProps {
  show: boolean;
  onHide: () => void;
}

const ContactModal: React.FC<ContactModalProps> = ({ show, onHide }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      // Submit to Formspree using AJAX as per their documentation
      const response = await fetch('https://formspree.io/f/mnqgoepp', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
        
        // Close modal after 2 seconds
        setTimeout(() => {
          setSubmitStatus(null);
          onHide();
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset status when modal is closed
  const handleHide = () => {
    setSubmitStatus(null);
    onHide();
  };

  return (
    <ModernModal show={show} onHide={handleHide}>
      <ModalHeader closeButton>
        <h5 id="exampleModalLongTitle">Get in Touch</h5>
      </ModalHeader>
      <ModalBody className="p-4">
        <div className="custom-form">
          {submitStatus === 'success' && (
            <div className="success-message">
              ✓ Thank you! Your message has been sent successfully.
            </div>
          )}
          {submitStatus === 'error' && (
            <div className="error-message">
              ✗ Sorry, there was an error sending your message. Please try again.
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-lg-12">
                <div className="form-group mb-4">
                  <input 
                    name="name" 
                    type="text" 
                    className="form-control" 
                    placeholder="Name" 
                    required 
                    disabled={isSubmitting}
                  />
                </div>
                <div className="form-group mb-4">
                  <input 
                    name="email" 
                    type="email" 
                    className="form-control" 
                    placeholder="Email" 
                    required 
                    disabled={isSubmitting}
                  />
                </div>
                <div className="form-group mb-4">
                  <input 
                    name="organization" 
                    type="text" 
                    className="form-control" 
                    placeholder="Organization (optional)" 
                    disabled={isSubmitting}
                  />
                </div>
                <div className="form-group mb-4">
                  <input 
                    name="subject" 
                    type="text" 
                    className="form-control" 
                    placeholder="Subject" 
                    disabled={isSubmitting}
                  />
                </div>
                <div className="form-group">
                  <textarea 
                    name="message" 
                    rows={4} 
                    className="form-control" 
                    placeholder="Your message or tell us about your use case..."
                    disabled={isSubmitting}
                  ></textarea>
                </div>
                <p className="mt-3">
                  We will get back to you within 24 hours.
                </p>
              </div>
            </div>
            <div className="row mt-3">
              <div className="col-sm-12 text-right">
                <button 
                  type="submit" 
                  className="btn btn-custom" 
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </ModalBody>
    </ModernModal>
  );
};

export default ContactModal;

